import type { ObjectDirective } from 'vue';

/**
 * Determine whether a click occurred outside the referenced
 * element(s) or not. Trigger the directive's handler if so.
 *
 * Listens on the element's own document and every same-origin ancestor document up to
 * the top window. This covers iframe-mounted elements: clicks inside the iframe (own
 * doc) and clicks in the surrounding parent shell (ancestor doc) both reach the
 * handler. Cross-origin ancestors are skipped — accessing them would throw.
 */

type ClickOutsideHandler = (e: MouseEvent) => void;

type ClickOutsideElement = HTMLElement & {
  _handler?: (e: MouseEvent) => void;
  _handlerDocs?: Document[];
};

function collectListenerDocuments(el: Element): Document[] {
  const docs: Document[] = [];
  const seen = new Set<Document>();
  let win: Window | null = el.ownerDocument?.defaultView ?? null;

  while (win) {
    try {
      const doc = win.document;

      if (doc && !seen.has(doc)) {
        seen.add(doc);
        docs.push(doc);
      }
    } catch {
      break;
    }

    if (win.parent === win) break;

    try {
      win = win.parent;
    } catch {
      break;
    }
  }

  return docs;
}

const clickoutside: ObjectDirective<ClickOutsideElement, ClickOutsideHandler> = {
  // `mounted`, not `beforeMount`: nodes inserted into a different document are adopted on
  // `appendChild`, which updates `ownerDocument`. Reading it before insertion returns the
  // runtime's document (where Vue created the node), not the iframe's.
  mounted(el, binding) {
    const handler = (e: MouseEvent) => {
      if (!el.contains(e.target as Node) && binding.value) {
        binding.value(e);
      }
    };

    el._handler = handler;
    el._handlerDocs = collectListenerDocuments(el);
    el._handlerDocs.forEach((doc) => doc.body.addEventListener('click', handler));
  },
  unmounted(el) {
    if (!el._handler) return;

    el._handlerDocs?.forEach((doc) => doc.body.removeEventListener('click', el._handler!));
    el._handler = undefined;
    el._handlerDocs = undefined;
  },
};

export default clickoutside;
